<template>
  <v-app class="referral-signup-app">
    <v-main>
      <div class="referral-signup">
        <div class="referral-signup__hero">
          <div class="referral-signup__brand">
            <v-img :src="logo" contain max-width="120" alt="BRPoints" class="mb-4" />
            <h1>Faça parte da rede BRPoints</h1>
            <p v-if="referralInfo" class="referral-signup__invite">
              Você foi indicado por <strong>{{ referralInfo.referrerName }}</strong><template v-if="showFranchiseCity"> · {{ referralInfo.franchiseCity }}</template>
            </p>
            <p v-else-if="!loadingInfo" class="referral-signup__invite">
              Complete seu cadastro para entrar na rede como associado aprovado.
            </p>
          </div>
        </div>

        <div class="referral-signup__panel">
          <v-card class="referral-signup__card" elevation="8">
            <v-card-text>
              <div v-if="loadingInfo" class="text-center py-8">
                <v-progress-circular indeterminate color="#e5183d" />
                <p class="mt-4 mb-0">Validando convite...</p>
              </div>

              <div v-else-if="infoError" class="referral-signup__state">
                <v-icon color="#c62828" size="42">mdi-link-off</v-icon>
                <h2>Convite inválido</h2>
                <p>{{ infoError }}</p>
                <v-btn text color="#e5183d" :to="{ name: 'login' }">Ir para o login</v-btn>
              </div>

              <div v-else-if="success" class="referral-signup__state">
                <v-icon color="#2dcc6b" size="48">mdi-check-circle</v-icon>
                <h2>Cadastro realizado!</h2>
                <p>
                  Seu perfil já está <strong>aprovado</strong> na rede.
                  Enviamos uma senha temporária para <strong>{{ form.email }}</strong>.
                </p>
                <v-btn color="#e5183d" dark :to="{ name: 'login' }">Acessar o painel</v-btn>
              </div>

              <template v-else>
                <div class="referral-signup__card-head">
                  <span class="referral-signup__step">Cadastro de associado</span>
                  <h2>Seus dados</h2>
                  <p>Preencha as informações abaixo. Você entrará direto como aprovado na rede.</p>
                </div>

                <v-form ref="formRef" v-model="valid" @submit.prevent="handleSubmit">
                  <v-row dense>
                    <v-col cols="12">
                      <v-text-field
                        v-model="form.name"
                        label="Nome completo"
                        outlined
                        dense
                        prepend-inner-icon="mdi-account-outline"
                        :rules="[rules.required]"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.document"
                        label="CPF"
                        outlined
                        dense
                        prepend-inner-icon="mdi-card-account-details-outline"
                        v-mask="'###.###.###-##'"
                        :rules="[rules.required, rules.cpf]"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.phone"
                        label="Telefone"
                        outlined
                        dense
                        prepend-inner-icon="mdi-phone-outline"
                        v-mask="'(##) #####-####'"
                        :rules="[rules.required]"
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="form.email"
                        label="E-mail"
                        type="email"
                        outlined
                        dense
                        prepend-inner-icon="mdi-email-outline"
                        :rules="[rules.required, rules.email]"
                      />
                    </v-col>
                  </v-row>

                  <v-alert type="info" dense outlined class="mt-2 mb-4 referral-signup__hint">
                    Ao concluir, você receberá por e-mail uma senha temporária para o primeiro acesso.
                  </v-alert>

                  <v-btn
                    type="submit"
                    block
                    large
                    color="#e5183d"
                    dark
                    :loading="submitting"
                    :disabled="!valid || submitting"
                  >
                    Entrar na rede
                  </v-btn>
                </v-form>
              </template>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </v-main>
  </v-app>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { GetReferralInfo, ReferralInfoType } from "@/services/referral/GetReferralInfo";
import { RegisterReferralAssociate } from "@/services/referral/RegisterReferralAssociate";

export default defineComponent({
  name: "ReferralSignupView",
  data() {
    return {
      logo: require("@/assets/logo.png"),
      loadingInfo: true,
      infoError: "",
      referralInfo: null as ReferralInfoType | null,
      valid: false,
      submitting: false,
      success: false,
      form: {
        name: "",
        document: "",
        phone: "",
        email: "",
      },
      rules: {
        required: (value: string) => !!String(value || "").trim() || "Campo obrigatório",
        email: (value: string) =>
          /.+@.+\..+/.test(String(value || "")) || "E-mail inválido",
        cpf: (value: string) => {
          const digits = String(value || "").replace(/\D/g, "");
          return digits.length === 11 || "CPF inválido";
        },
      },
    };
  },
  computed: {
    referralCode(): string {
      return String(this.$route.params.code || "").trim().toUpperCase();
    },
    showFranchiseCity(): boolean {
      if (!this.referralInfo?.franchiseCity) return false;
      const city = this.referralInfo.franchiseCity.trim();
      const referrer = this.referralInfo.referrerName.trim();
      return city.length > 0 && city.toLowerCase() !== referrer.toLowerCase();
    },
  },
  mounted() {
    void this.loadReferralInfo();
  },
  methods: {
    async loadReferralInfo() {
      if (!this.referralCode) {
        this.infoError = "Link de indicação incompleto.";
        this.loadingInfo = false;
        return;
      }

      try {
        this.referralInfo = await new GetReferralInfo().execute(this.referralCode);
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Este link de indicação não existe ou expirou.";
        this.infoError = message;
      } finally {
        this.loadingInfo = false;
      }
    },
    async handleSubmit() {
      const formRef = this.$refs.formRef as { validate?: () => boolean } | undefined;
      if (formRef?.validate && !formRef.validate()) return;

      this.submitting = true;
      try {
        await new RegisterReferralAssociate().execute({
          referralCode: this.referralCode,
          name: this.form.name.trim(),
          document: this.form.document,
          phone: this.form.phone,
          email: this.form.email.trim(),
        });
        this.success = true;
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Não foi possível concluir o cadastro.";
        this.$store.dispatch("showSnack", { message, color: "red" });
      } finally {
        this.submitting = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
.referral-signup-app {
  background: #f6f8fc;
}

.referral-signup {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1.05fr;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
}

.referral-signup__hero {
  background: linear-gradient(145deg, #1a1a2e 0%, #242968 55%, #e5183d 120%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 32px;
}

.referral-signup__brand {
  max-width: 420px;

  h1 {
    font-size: 32px;
    line-height: 1.2;
    margin-bottom: 12px;
  }
}

.referral-signup__invite {
  font-size: 16px;
  line-height: 1.5;
  opacity: 0.92;
}

.referral-signup__panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
}

.referral-signup__card {
  width: 100%;
  max-width: 520px;
  border-radius: 18px !important;
}

.referral-signup__card-head {
  margin-bottom: 18px;

  h2 {
    margin: 6px 0 4px;
    font-size: 24px;
    color: #1f243f;
  }

  p {
    margin: 0;
    color: #667085;
    font-size: 14px;
  }
}

.referral-signup__step {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #e5183d;
}

.referral-signup__hint {
  font-size: 13px;
}

.referral-signup__state {
  text-align: center;
  padding: 24px 8px;

  h2 {
    margin: 12px 0 8px;
    color: #1f243f;
  }

  p {
    color: #667085;
    margin-bottom: 18px;
  }
}
</style>
